import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';

import { Countries } from '@shared/classes/countries';
import { Ranking } from './interfaces/ranking';
import { RankingDataService } from './services/ranking-data.service';
import { USStates } from '@shared/classes/usstates';
import { WINDOW } from '@shared/services/window.service';

import { environment } from 'environments/environment';

// Icons
import { faEquals, faHashtag, faRankingStar, faSortDown, faSortUp, faSpinner } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'home-ranking',
  templateUrl: './ranking.component.html',
  styleUrls: ['./ranking.component.scss'],
  standalone: false
})
export class RankingComponent {

  countries = Countries;
  environment = environment;
  fullText = '';
  isDescriptionExpanded = false;
  isRankingsMore = false;
  rankings: Ranking[] = [];
  rankings2Show: Ranking[] = [];
  rankingsLimit = 0;
  rankingsLimitStep = 50;
  states = USStates;

  // Icons
  faEquals = faEquals;
  faHashtag = faHashtag;
  faRankingStar = faRankingStar;
  faSortDown = faSortDown;
  faSortUp = faSortUp;
  faSpinner = faSpinner;

  // Progress
  errorReading: string | null = null;
  isReading = false;

  // Sorting
  sortColumn = 'Position';
  sortOrderDesc = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private rankingDataService: RankingDataService,
    @Inject(WINDOW) private window: Window | null
  ) { }

  ngOnInit() {

    if (isPlatformBrowser(this.platformId)) {
      this.read();
    } else {
      this.isReading = true;
    }
  }

  expandDescription() {
    this.isDescriptionExpanded = true;
  }

  goProfile(studentId: number) {
    if (this.window) {
      this.window.open('/profile?s=' + studentId, '_blank');
    }
  }

  onChangeFullText() {
    this.filterByFullText();
    const total = this.rankings2Show.length;
    this.rankings2Show = this.rankings2Show.slice(0, this.rankingsLimit);
    this.isRankingsMore = this.rankings2Show.length < total;
  }

  showMoreRankings() {
    this.rankingsLimit += this.rankingsLimitStep;
    this.onChangeFullText();
  }

  sort(column: string) {
    if (this.sortColumn === column) {
      this.sortOrderDesc = !this.sortOrderDesc;
    } else {
      this.sortColumn = column;
      this.sortOrderDesc = false;
    }

    // Refilter and sort
    this.onChangeFullText();
  }

  private filterByFullText() {

    if (!this.fullText.length) {
      this.rankings2Show = this.rankings;
    } else {
      this.rankings2Show = this.rankings.filter((ranking: Ranking) => ranking.FirstName.toLowerCase().includes(this.fullText.toLowerCase()) || ranking.Account.toLowerCase().includes(this.fullText.toLowerCase()));
    }

    // Sort
    this.rankings2Show.sort((a: Ranking, b: Ranking) => {
      return a[this.sortColumn] > b[this.sortColumn] ? (this.sortOrderDesc ? -1 : 1) : (this.sortOrderDesc ? 1 : -1);
    });
  }

  private processRankings(value) {

    // Store the rankings
    this.rankings = value;

    // Process values
    this.rankings.forEach(ranking => {
      if (ranking.State === '-') {
        ranking.State = null;
      }

      ranking.Location = ((ranking.Country === 'US' ? 'USA' : this.countries.getCountryName(ranking.Country)) || '') + (ranking.State ? ', ' + this.states.getStateName(ranking.State) : '') + (ranking.City ? ', ' + ranking.City : '');
    });

    // Show initial page of rankings
    this.showMoreRankings();
  }

  private read() {

    if (this.isReading) {
      return;
    }

    // Show spinner
    this.isReading = true;

    // Reset error status
    this.errorReading = null;

    // Read the ranking data
    this.rankingDataService.read()
      .subscribe(
        (value) => {

            this.processRankings(value);

            // Hide spinner
            this.isReading = false;
        },
        (error) => {
            this.errorReading = error;
            this.isReading = false;
        }
      );
  }
}
